// © 2026 AttainIP Engineering

/**
 * Inline markers embedded in persisted assistant messages.
 *
 * The chat surfaces use the same ``::name[args]::`` shape as the
 * RFC-0153 waypoint protocol so the parsing logic is consistent. These
 * markers survive history reload — tool_call/tool_result rows are not
 * persisted, so anything we want the chat panel to render after
 * reload (chips, badges, …) has to be encoded into the message body.
 *
 * Today's marker:
 *
 *   ``::thought[<base64>]::``
 *
 *   where ``<base64>`` is base64(JSON) of
 *
 *     {
 *       "s": <seconds>,
 *       "trace": [
 *         {"k":"tool","n":"<name>","x":"<subject>"?},
 *         {"k":"say","s":"<intermediate-narration-text>"},
 *         …
 *       ]
 *     }
 *
 *   Renders as a "Thought for Ns" chip above the assistant bubble; the
 *   chip's expand panel walks the trace in order so the user sees the
 *   same reasoning steps they saw live (tool actions + intermediate
 *   narrations). Base64 keeps the marker robust to any text content
 *   (newlines, brackets, pipes) that might appear in narrations.
 */

const THOUGHT_MARKER = /::thought\[([A-Za-z0-9+/=]+)\]::\s*/;

export interface ThoughtTraceTool {
  kind: 'tool';
  name: string;
  subject?: string;
}

export interface ThoughtTraceSay {
  kind: 'say';
  text: string;
}

export type ThoughtTraceItem = ThoughtTraceTool | ThoughtTraceSay;

export interface ThoughtMarker {
  /** Elapsed seconds parsed from the marker. */
  seconds: number;
  /** Per-step trace captured at save time. May be empty. */
  trace: ThoughtTraceItem[];
}

export interface ParsedAssistantContent {
  /** Message body with the marker stripped — safe to render. */
  content: string;
  /** Marker metadata, if one was present. */
  thought: ThoughtMarker | null;
}

function decodeBase64(input: string): string | null {
  try {
    if (typeof globalThis.atob === 'function') {
      // Browser path. atob returns a binary string; reinterpret as UTF-8.
      const binary = globalThis.atob(input);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return new TextDecoder().decode(bytes);
    }
  } catch {
    return null;
  }
  return null;
}

interface RawThoughtItem {
  k?: unknown;
  n?: unknown;
  x?: unknown;
  s?: unknown;
}

function parseTrace(raw: unknown): ThoughtTraceItem[] {
  if (!Array.isArray(raw)) return [];
  const out: ThoughtTraceItem[] = [];
  for (const item of raw as RawThoughtItem[]) {
    if (!item || typeof item !== 'object') continue;
    if (item.k === 'tool' && typeof item.n === 'string' && item.n) {
      const tool: ThoughtTraceTool = { kind: 'tool', name: item.n };
      if (typeof item.x === 'string' && item.x) tool.subject = item.x;
      out.push(tool);
    } else if (item.k === 'say' && typeof item.s === 'string' && item.s) {
      out.push({ kind: 'say', text: item.s });
    }
  }
  return out;
}

/**
 * Pull the ``::thought[…]::`` marker out of an assistant message body
 * if present. Returns the cleaned content + parsed metadata, or the
 * original content with ``thought = null`` when the marker is missing
 * or unparseable.
 *
 * The marker only matches at the very start (with optional whitespace
 * after) so prose containing similar text further into the body is
 * safe.
 */
export function parseAssistantContent(content: string): ParsedAssistantContent {
  if (!content) return { content, thought: null };
  const match = content.match(THOUGHT_MARKER);
  if (!match || match.index !== 0) {
    return { content, thought: null };
  }

  const decoded = decodeBase64(match[1]);
  if (!decoded) return { content, thought: null };

  let payload: unknown;
  try {
    payload = JSON.parse(decoded);
  } catch {
    return { content, thought: null };
  }
  if (!payload || typeof payload !== 'object') return { content, thought: null };

  const seconds = Number((payload as { s?: unknown }).s);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return { content, thought: null };
  }
  const trace = parseTrace((payload as { trace?: unknown }).trace);

  return {
    content: content.slice(match[0].length).replace(/^\s+/, ''),
    thought: { seconds, trace },
  };
}
